import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useStudyStore } from '../../store/useStudyStore'
import { GameResult } from '../../types/database'
import { Trophy, Clock, Target, TrendingUp, Calendar, Award, ExternalLink } from 'lucide-react'

interface GameHistoryProps {
  gameId?: string
  limit?: number
}

/**
 * Component hiển thị lịch sử chơi game
 */
export const GameHistory: React.FC<GameHistoryProps> = ({ gameId, limit }) => {
  const { getGameResults, getGameAttempts } = useStudyStore()
  
  const results = gameId 
    ? getGameResults(gameId).slice(0, limit) 
    : getGameResults().slice(0, limit)
  
  const attempts = gameId ? getGameAttempts(gameId) : 0

  // Không hiển thị gì khi chưa có dữ liệu (giữ giao diện gọn gàng)
  if (results.length === 0) {
    return null
  }

  // Stats
  const totalGames = results.length
  const wonGames = results.filter(r => r.won).length
  const winRate = (wonGames / totalGames) * 100
  const avgScore = results.reduce((sum, r) => sum + r.percentage, 0) / totalGames
  const avgTime = results.reduce((sum, r) => sum + r.timeSpent, 0) / totalGames
  const bestScore = Math.max(...results.map(r => r.percentage))

  return (
    <div className="space-y-4">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard 
          icon={<Trophy className="w-5 h-5" />}
          label="Tỷ lệ thắng"
          value={`${winRate.toFixed(0)}%`}
          color="text-yellow-600 bg-yellow-50"
        />
        <StatCard 
          icon={<Target className="w-5 h-5" />}
          label="Điểm TB"
          value={`${avgScore.toFixed(0)}%`}
          color="text-blue-600 bg-blue-50"
        />
        <StatCard 
          icon={<Clock className="w-5 h-5" />}
          label="Thời gian TB"
          value={formatTime(avgTime)}
          color="text-green-600 bg-green-50"
        />
        <StatCard 
          icon={<TrendingUp className="w-5 h-5" />}
          label="Điểm cao nhất"
          value={`${bestScore.toFixed(0)}%`}
          color="text-purple-600 bg-purple-50"
        />
      </div>

      {/* Results List */}
      <div className="bg-white rounded-xl shadow-md p-4 space-y-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Award className="w-5 h-5 text-orange-500" />
            Lịch sử chơi ({totalGames} lần)
          </h3>
          <Link
            to="/thong-ke-game"
            className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
          >
            <Trophy className="w-4 h-4" />
            <span>Xếp hạng</span>
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>

        {results.map((result, index) => (
          <GameResultCard key={result.id} result={result} index={index} />
        ))}
      </div>
    </div>
  )
}

/**
 * Stat Card Component
 */
const StatCard: React.FC<{
  icon: React.ReactNode
  label: string
  value: string
  color: string
}> = ({ icon, label, value, color }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    className={`${color} rounded-lg p-3`}
  >
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <span className="text-xs font-medium">{label}</span>
    </div>
    <div className="text-xl font-bold">{value}</div>
  </motion.div>
)

/**
 * Game Result Card Component
 */
const GameResultCard: React.FC<{
  result: GameResult
  index: number
}> = ({ result, index }) => {
  const getGradeColor = (percentage: number) => {
    if (percentage >= 90) return 'text-green-600 bg-green-50'
    if (percentage >= 70) return 'text-blue-600 bg-blue-50'
    if (percentage >= 50) return 'text-orange-600 bg-orange-50'
    return 'text-red-600 bg-red-50'
  }

  const getGradeText = (percentage: number) => {
    if (percentage >= 90) return 'Xuất sắc'
    if (percentage >= 70) return 'Tốt'
    if (percentage >= 50) return 'Khá'
    return 'Cần cố gắng'
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`border rounded-lg p-3 ${
        result.won 
          ? 'border-green-200 bg-green-50/30' 
          : 'border-gray-200 bg-gray-50/30'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {result.won ? (
              <Trophy className="w-4 h-4 text-yellow-500" />
            ) : (
              <div className="w-4 h-4" />
            )}
            <span className="font-semibold text-sm">
              {result.won ? '🎉 Chiến thắng' : '❌ Chưa hoàn thành'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${getGradeColor(result.percentage)}`}>
              {getGradeText(result.percentage)}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <Target className="w-3 h-3" />
              <span>Điểm: {result.score}/{result.maxScore} ({result.percentage.toFixed(0)}%)</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{formatTime(result.timeSpent)}</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              <span>Phòng: {result.roomsCompleted}/{result.maxScore}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(result.completedAt)}</span>
            </div>
          </div>
        </div>

        {/* Score Badge */}
        <div className={`ml-3 px-4 py-2 rounded-lg text-center ${getGradeColor(result.percentage)}`}>
          <div className="text-2xl font-bold">{result.percentage.toFixed(0)}</div>
          <div className="text-xs">%</div>
        </div>
      </div>

      {/* Detailed Answers (Expandable - Optional) */}
      {/* You can add expansion logic here if needed */}
    </motion.div>
  )
}

/**
 * Format time from seconds to mm:ss
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Format date to readable string
 */
const formatDate = (date: Date): string => {
  const d = new Date(date)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const hours = diff / (1000 * 60 * 60)
  
  if (hours < 1) {
    return 'Vừa xong'
  } else if (hours < 24) {
    return `${Math.floor(hours)}h trước`
  } else {
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })
  }
}

